import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from "@angular/common/http";
import {
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
} from "@angular/core";
import { MAT_DATE_FORMATS } from "@angular/material/core";
import { BrowserModule } from "@angular/platform-browser";
import {
  BrowserAnimationsModule,
  provideAnimations,
} from "@angular/platform-browser/animations";
import { provideRouter, withInMemoryScrolling } from "@angular/router";
import { provideServiceWorker } from "@angular/service-worker";
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalService,
} from "@azure/msal-angular";
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from "@azure/msal-browser";
import { provideFuse } from "@fuse";
import { AngularDeviceInformationService } from "angular-device-information";
import {
  apiConfig,
  clientId,
  production,
  ssoUrl,
} from "environments/environment.helper";
import { NgxFileDropModule } from "ngx-file-drop";
import {
  provideCacheableAnimationLoader,
  provideLottieOptions,
} from "ngx-lottie";
import { provideNgxMask } from "ngx-mask";
import { appRoutes } from "./app.routes";
import { provideAuth } from "./core/auth/interceptors/auth.provider";
import { fuseLayoutConfig } from "./core/config/app.config";
import { provideIcons } from "./core/icons/icons.provider";
import { MockApiService } from "./mock-api";
import { LuxonDateAdapter } from "./shared/adapters/luxon-date-adapter";

export function loggerCallback(logLevel: LogLevel, message: string) {}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: clientId,
      authority: "https://login.microsoftonline.com/aemenersol.com",
      redirectUri: "/",
      postLogoutRedirectUri: `${ssoUrl}/login`,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(apiConfig.uri, apiConfig.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...apiConfig.scopes],
    },
    loginFailedRoute: "/login",
  };
}

export const mainConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideAuth(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({ scrollPositionRestoration: "enabled" })
    ),
    provideAnimations(),
    provideIcons(),
    provideNgxMask(),
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: "D",
        },
        display: {
          dateInput: "DDD",
          monthYearLabel: "LLL yyyy",
          dateA11yLabel: "DD",
          monthYearA11yLabel: "LLLL yyyy",
        },
      },
    },
    provideFuse({
      mockApi: {
        delay: 0,
        service: MockApiService,
      },
      fuse: fuseLayoutConfig,
    }),
    AngularDeviceInformationService,
    provideServiceWorker("ngsw-worker.js", {
      enabled: production,
      registrationStrategy: "registerWhenStable:30000",
    }),
    importProvidersFrom(
      BrowserModule,
      NgxFileDropModule,
      BrowserAnimationsModule
    ),
    provideLottieOptions({
      player: () => import("lottie-web"),
    }),
    provideCacheableAnimationLoader(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    LuxonDateAdapter,
  ],
};
